import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Trash2, ListChecks, Filter, FileText, Search, Download } from 'lucide-react';
import InternalOnlyHint from '@/components/shared/InternalOnlyHint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// TikTok / YouTube are paused for now; existing tasks keep their platform.
const PLATFORMS = ['Instagram', 'Infact999', 'Etkinlik Alanı'];

// "#kampanya, #infact" / "@marka" → ['kampanya', 'infact']
const parseTagList = (value, prefix) => (value || '')
  .split(/[\s,]+/)
  .map((item) => item.trim().replace(new RegExp(`^\\${prefix}+`), ''))
  .filter(Boolean);
const TASK_TYPES = [
  // Genel
  'Takip ve Abonelik',
  'Tekrar Paylaşma',
  'Fiziksel Katılım',
  'Eğitim ve Strateji',
  'Hazır Görsel',
  'Özgün Görsel',
  'Kısa Özgün Video',
  'Uzun Özgün Video',
  // Instagram
  'Instagram Story',
  'Instagram Post',
  'Instagram Carousel Post',
  'Instagram Live',
  'Instagram Guides',
  'Instagram Reels'
];
const CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin',
  'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa',
  'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan',
  'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Isparta',
  'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir',
  'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla',
  'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop',
  'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak', 'Van',
  'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale', 'Batman', 'Şırnak',
  'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce', 'KKTC'
];
const isPoolPayment = (paymentType) => ['Gelir Havuzu', 'Bütçe Havuzu', 'Gelir Payı', 'Bütçe Payı'].includes(paymentType);
const formatMoney = (value) => Number(value ?? 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const defaultTaskPricingConfig = {
  A0: 0,
  B0: 0,
  a: 0.5,
  S0: 10000,
  C0: 100,
  alpha: 0.5,
  beta: 0.5,
  reach_quality: { lambda: 0.5, gamma: 1, kpis: [] },
  perf_quality: { lambda: 0.5, gamma: 1, kpis: [] },
};

const clonePricingConfig = (config = defaultTaskPricingConfig) => JSON.parse(JSON.stringify(config));
const normalizePricingConfig = (config = {}) => ({
  ...clonePricingConfig(defaultTaskPricingConfig),
  ...config,
  reach_quality: {
    ...defaultTaskPricingConfig.reach_quality,
    ...(config.reach_quality || {}),
    kpis: Array.isArray(config.reach_quality?.kpis) ? config.reach_quality.kpis : [],
  },
  perf_quality: {
    ...defaultTaskPricingConfig.perf_quality,
    ...(config.perf_quality || {}),
    kpis: Array.isArray(config.perf_quality?.kpis) ? config.perf_quality.kpis : [],
  },
});

const validatePricingConfig = (config) => {
  const checks = [
    ['A0', config.A0, 0, Infinity],
    ['B0', config.B0, 0, Infinity],
    ['a', config.a, 0, 1],
    ['S0', config.S0, 0.000001, Infinity],
    ['C0', config.C0, 0.000001, Infinity],
    ['alpha', config.alpha, 0, 2],
    ['beta', config.beta, 0, 2],
  ];
  for (const [label, value, min, max] of checks) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < min || n > max) return `${label} geçerli aralıkta değil`;
  }
  for (const [key, label] of [['reach_quality', 'Erişim'], ['perf_quality', 'Performans']]) {
    const q = config[key] || {};
    if (Number(q.lambda) < 0 || Number(q.lambda) > 1) return `${label} lambda 0-1 arasında olmalı`;
    if (Number(q.gamma) < 0 || Number(q.gamma) > 3) return `${label} gamma 0-3 arasında olmalı`;
    const kpis = Array.isArray(q.kpis) ? q.kpis : [];
    if (kpis.length === 0) continue;
    const total = kpis.reduce((sum, kpi) => sum + Number(kpi.weight || 0), 0);
    if (Math.abs(total - 100) > 0.001) return `${label} KPI ağırlıkları toplamı 100 olmalı`;
    if (kpis.some(kpi => !String(kpi.name || '').trim() || Number(kpi.weight || 0) <= 0)) {
      return `${label} KPI adı ve ağırlığı zorunlu`;
    }
  }
  return null;
};

const emptyForm = {
  campaign_id: '',
  title: '',
  short_description: '',
  long_description: '',
  task_types: [],
  platform: '',
  capacity: '',
  deadline: '',
  payment_amount: '',
  min_instagram_followers: '',
  min_tiktok_followers: '',
  min_youtube_followers: '',
  required_city: '',
  min_age: '',
  max_age: '',
  submission_type: 'link',
  required_hashtags: '',
  required_mentions: '',
  pricing_config: clonePricingConfig(),
};

export default function AdminTasks() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterTitle, setFilterTitle] = useState('');
  const [filterTaskType, setFilterTaskType] = useState('all');
  const [filterCity, setFilterCity] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch campaigns
      const { data: campaignsData } = await supabase
        .from('campaigns')
        .select('id, title, start_date, end_date, payment_type')
        .order('created_at', { ascending: false });
      setCampaigns(campaignsData || []);

      // Fetch tasks with campaign info
      const { data: tasksData, error } = await supabase
        .from('tasks')
        .select('*, campaigns(title, payment_type)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch completed counts for each task
      const tasksWithCounts = await Promise.all((tasksData || []).map(async (task) => {
        const { count } = await supabase
          .from('task_submissions')
          .select('id', { count: 'exact', head: true })
          .eq('task_id', task.id)
          .eq('status', 'approved');
        return { ...task, completed_count: count || 0 };
      }));
      
      setTasks(tasksWithCounts);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredTasks = tasks.filter(task => {
    if (filterCampaign !== 'all' && task.campaign_id !== filterCampaign) return false;
    if (filterPlatform !== 'all' && task.platform !== filterPlatform) return false;
    if (filterTitle && !task.title?.toLowerCase().includes(filterTitle.toLowerCase())) return false;
    if (filterTaskType !== 'all' && !task.task_types?.includes(filterTaskType)) return false;
    if (filterCity !== 'all' && task.required_city !== filterCity) return false;
    return true;
  });
  const selectedCampaignForForm = campaigns.find(c => c.id === form.campaign_id);
  const formIsPool = isPoolPayment(selectedCampaignForForm?.payment_type);
  const formPricingConfig = normalizePricingConfig(form.pricing_config);

  const openCreateDialog = () => {
    setSelectedTask(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (task) => {
    setSelectedTask(task);
    setForm({
      campaign_id: task.campaign_id || '',
      title: task.title || '',
      short_description: task.short_description || '',
      long_description: task.long_description || '',
      task_types: task.task_types || [],
      platform: task.platform || '',
      capacity: task.capacity || '',
      deadline: task.deadline ? task.deadline.split('T')[0] : '',
      payment_amount: task.payment_amount || '',
      min_instagram_followers: task.min_instagram_followers || '',
      min_tiktok_followers: task.min_tiktok_followers || '',
      min_youtube_followers: task.min_youtube_followers || '',
      required_city: task.required_city || '',
      min_age: task.min_age || '',
      max_age: task.max_age || '',
      submission_type: task.submission_type || 'link',
      required_hashtags: (task.required_hashtags || []).map((t) => `#${t}`).join(' '),
      required_mentions: (task.required_mentions || []).map((m) => `@${m}`).join(' '),
      pricing_config: normalizePricingConfig(task.pricing_config || {}),
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (task) => {
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTaskTypeToggle = (type) => {
    setForm(prev => ({
      ...prev,
      task_types: prev.task_types.includes(type)
        ? prev.task_types.filter(t => t !== type)
        : [...prev.task_types, type]
    }));
  };

  const updatePricingConfig = (field, value) => {
    setForm(prev => ({
      ...prev,
      pricing_config: {
        ...normalizePricingConfig(prev.pricing_config),
        [field]: value,
      },
    }));
  };

  const updateQualityConfig = (block, field, value) => {
    setForm(prev => {
      const pricing = normalizePricingConfig(prev.pricing_config);
      return {
        ...prev,
        pricing_config: {
          ...pricing,
          [block]: {
            ...pricing[block],
            [field]: value,
          },
        },
      };
    });
  };

  const updateQualityKpi = (block, index, field, value) => {
    setForm(prev => {
      const pricing = normalizePricingConfig(prev.pricing_config);
      const kpis = [...(pricing[block]?.kpis || [])];
      kpis[index] = { ...kpis[index], [field]: value };
      return {
        ...prev,
        pricing_config: {
          ...pricing,
          [block]: { ...pricing[block], kpis },
        },
      };
    });
  };

  const addQualityKpi = (block) => {
    setForm(prev => {
      const pricing = normalizePricingConfig(prev.pricing_config);
      return {
        ...prev,
        pricing_config: {
          ...pricing,
          [block]: {
            ...pricing[block],
            kpis: [...(pricing[block]?.kpis || []), { name: '', weight: 100 }],
          },
        },
      };
    });
  };

  const removeQualityKpi = (block, index) => {
    setForm(prev => {
      const pricing = normalizePricingConfig(prev.pricing_config);
      return {
        ...prev,
        pricing_config: {
          ...pricing,
          [block]: {
            ...pricing[block],
            kpis: (pricing[block]?.kpis || []).filter((_, i) => i !== index),
          },
        },
      };
    });
  };

  const handleSave = async () => {
    if (!form.campaign_id || !form.title || !form.platform || !form.capacity) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setSaving(true);
    try {
      // Get campaign dates for validation
      const campaign = campaigns.find(c => c.id === form.campaign_id);
      if (!campaign) {
        toast.error('Kampanya bulunamadı');
        return;
      }

      // For non-volunteer campaigns, payment_amount defaults to 0 if not entered
      const isVolunteerCampaign = campaign.payment_type === 'Gönüllü';
      let paymentAmount = null;
      if (!isVolunteerCampaign) {
        paymentAmount = (form.payment_amount !== '' && form.payment_amount !== null && form.payment_amount !== undefined) 
          ? parseFloat(form.payment_amount) 
          : 0;
      }
      if (isPoolPayment(campaign.payment_type) && (!paymentAmount || paymentAmount <= 0)) {
        toast.error('Havuz kampanyalarında görev baz TL 0’dan büyük olmalı');
        setSaving(false);
        return;
      }
      const pricingConfig = normalizePricingConfig(form.pricing_config);
      if (isPoolPayment(campaign.payment_type)) {
        const pricingError = validatePricingConfig(pricingConfig);
        if (pricingError) {
          toast.error(pricingError);
          setSaving(false);
          return;
        }
      }

      const taskData = {
        campaign_id: form.campaign_id,
        title: form.title,
        short_description: form.short_description || null,
        long_description: form.long_description || null,
        task_types: form.task_types.length > 0 ? form.task_types : ['Diğer'],
        platform: form.platform,
        capacity: parseInt(form.capacity),
        deadline: form.deadline ? new Date(form.deadline).toISOString() : campaign.end_date,
        payment_amount: paymentAmount,
        min_instagram_followers: form.min_instagram_followers ? parseInt(form.min_instagram_followers) : null,
        min_tiktok_followers: null,
        min_youtube_followers: null,
        required_city: form.required_city || null,
        min_age: form.min_age ? parseInt(form.min_age) : null,
        max_age: form.max_age ? parseInt(form.max_age) : null,
        submission_type: form.submission_type || 'link',
        required_hashtags: form.platform === 'Instagram' ? parseTagList(form.required_hashtags, '#') : [],
        required_mentions: form.platform === 'Instagram' ? parseTagList(form.required_mentions, '@') : [],
        pricing_config: isPoolPayment(campaign.payment_type) ? pricingConfig : clonePricingConfig(),
        updated_at: new Date().toISOString(),
      };

      if (selectedTask) {
        // Update existing task
        const { error } = await supabase
          .from('tasks')
          .update(taskData)
          .eq('id', selectedTask.id);

        if (error) throw error;
        toast.success('Görev güncellendi');
      } else {
        // Create new task
        taskData.status = 'open';
        taskData.created_at = new Date().toISOString();
        
        const { error } = await supabase
          .from('tasks')
          .insert(taskData);

        if (error) throw error;
        toast.success('Görev oluşturuldu');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Görev kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', selectedTask.id);

      if (error) throw error;

      toast.success('Görev silindi');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Görev silinirken hata oluştu');
    }
  };

  const toggleStatus = async (task) => {
    try {
      const now = Date.now();
      const deadlineTime = task.deadline ? new Date(task.deadline).getTime() : null;
      const isExpired = deadlineTime && deadlineTime < now;
      const isClosing = task.status === 'open';
      const newStatus = isClosing || isExpired ? 'closed' : 'open';
      const updateData = {
        status: newStatus,
        manually_closed: isClosing,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', task.id);

      if (error) throw error;

      if (!isClosing && isExpired) {
        toast.warning('Görev son tarihi geçtiği için açılamaz');
      } else {
        toast.success(`Görev ${newStatus === 'open' ? 'açıldı' : 'kapatıldı'}`);
      }
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Durum değiştirilirken hata oluştu');
    }
  };

  const getStatusBadge = (status) => {
    return status === 'open' ? (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Açık</Badge>
    ) : (
      <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Kapalı</Badge>
    );
  };

  const getPlatformBadge = (platform) => {
    const colors = {
      'Instagram': 'bg-pink-100 text-pink-800',
      'Tiktok': 'bg-slate-100 text-slate-800',
      'Youtube': 'bg-red-100 text-red-800',
      'Infact999': 'bg-brand-yellow-soft text-brand-ink',
      'Etkinlik Alanı': 'bg-brand-neon-soft text-brand-ink',
    };
    return (
      <Badge className={`${colors[platform] || 'bg-gray-100 text-gray-800'} hover:${colors[platform]}`}>
        {platform}
      </Badge>
    );
  };

  return (
    <div data-testid="admin-tasks">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-brand text-2xl text-brand-ink">Görev Yönetimi</h2>
        <Button onClick={openCreateDialog} data-testid="create-task-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Görev
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-white rounded-lg border border-gray-200">
        <Filter className="w-4 h-4 text-gray-500" />
        
        {/* Title Search */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Başlık:</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Ara..."
              value={filterTitle}
              onChange={(e) => setFilterTitle(e.target.value)}
              className="w-32 pl-8"
              data-testid="filter-title-input"
            />
          </div>
        </div>
        
        {/* Campaign Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Kampanya:</Label>
          <Select value={filterCampaign} onValueChange={setFilterCampaign}>
            <SelectTrigger className="w-48" data-testid="filter-campaign-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {campaigns.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Platform Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Platform:</Label>
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-36" data-testid="filter-platform-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {PLATFORMS.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Task Type Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Görev Türü:</Label>
          <Select value={filterTaskType} onValueChange={setFilterTaskType}>
            <SelectTrigger className="w-44" data-testid="filter-task-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {TASK_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* City Filter */}
        <div className="flex items-center gap-2">
          <Label className="text-sm">Şehir:</Label>
          <Select value={filterCity} onValueChange={setFilterCity}>
            <SelectTrigger className="w-36" data-testid="filter-city-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {CITIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <ListChecks className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {tasks.length === 0 ? 'Henüz görev bulunmuyor' : 'Filtrelere uygun görev bulunamadı'}
          </p>
          {tasks.length === 0 && (
            <Button onClick={openCreateDialog} className="mt-4">
              <Plus className="w-4 h-4 mr-2" />
              İlk Görevi Oluştur
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <Table data-testid="tasks-table">
            <TableHeader>
              <TableRow>
                <TableHead>Başlık</TableHead>
                <TableHead>Kampanya</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Kontenjan</TableHead>
                <TableHead>Son Tarih</TableHead>
                <TableHead>Ödeme</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.map((task) => (
                <TableRow key={task.id} data-testid={`task-row-${task.id}`}>
                  <TableCell className="font-medium">{task.title}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {task.campaigns?.title || '-'}
                  </TableCell>
                  <TableCell>{getPlatformBadge(task.platform)}</TableCell>
                  <TableCell className="font-[Lato] text-sm">
                    {task.completed_count || 0} / {task.capacity}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {task.deadline ? new Date(task.deadline).toLocaleDateString('tr-TR') : '-'}
                  </TableCell>
                  <TableCell className="font-[Lato]">
                    {task.payment_amount ? `${formatMoney(task.payment_amount)} TL${isPoolPayment(task.campaigns?.payment_type) ? '+' : ''}` : '-'}
                  </TableCell>
                  <TableCell>
                    <button onClick={() => toggleStatus(task)} className="cursor-pointer">
                      {getStatusBadge(task.status)}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/tasks/${task.id}/report`)}
                        title="Katılımcı Raporu"
                        data-testid={`report-task-${task.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(task)}
                        data-testid={`edit-task-${task.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(task)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTask ? 'Görevi Düzenle' : 'Yeni Görev Oluştur'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campaign Selection */}
            <div className="space-y-2">
              <Label>Kampanya *</Label>
              <Select value={form.campaign_id} onValueChange={(v) => handleInputChange('campaign_id', v)}>
                <SelectTrigger data-testid="task-campaign-select">
                  <SelectValue placeholder="Kampanya seçin" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Başlık *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                maxLength={17}
                placeholder="Görev başlığı (max 17 karakter)"
                data-testid="task-title-input"
              />
            </div>

            {/* Short Description */}
            <div className="space-y-2">
              <Label htmlFor="short_description">Kısa Açıklama</Label>
              <Input
                id="short_description"
                value={form.short_description}
                onChange={(e) => handleInputChange('short_description', e.target.value)}
                maxLength={99}
                placeholder="Kısa açıklama"
                data-testid="task-short-desc-input"
              />
            </div>

            {/* Long Description */}
            <div className="space-y-2">
              <Label htmlFor="long_description">Görev Uzun Açıklaması (max 999 karakter)</Label>
              <Textarea
                id="long_description"
                value={form.long_description}
                onChange={(e) => handleInputChange('long_description', e.target.value)}
                maxLength={999}
                rows={4}
                placeholder="Görev detayları ve gereksinimler..."
                className="resize-none"
                data-testid="task-long-desc-input"
              />
              <p className="text-xs text-gray-500 text-right">{form.long_description?.length || 0}/999</p>
            </div>

            {/* Platform & Task Types */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Platform *</Label>
                <Select value={form.platform} onValueChange={(v) => handleInputChange('platform', v)}>
                  <SelectTrigger data-testid="task-platform-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set([...PLATFORMS, form.platform].filter(Boolean))].map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Görev Türleri
                  <InternalOnlyHint title="Uygulamada gösterilmez; yalnızca admin listesinde filtre için kullanılır." />
                </Label>
                <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-24 overflow-y-auto">
                  {TASK_TYPES.map(type => (
                    <label key={type} className="flex items-center gap-1 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.task_types.includes(type)}
                        onCheckedChange={() => handleTaskTypeToggle(type)}
                        data-testid={`task-type-${type}`}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {form.platform === 'Instagram' && (
              <div className="grid grid-cols-2 gap-4 rounded-lg border border-pink-100 bg-pink-50/40 p-3">
                <div className="space-y-2">
                  <Label htmlFor="required_hashtags">Zorunlu Hashtag'ler</Label>
                  <Input
                    id="required_hashtags"
                    value={form.required_hashtags}
                    onChange={(e) => handleInputChange('required_hashtags', e.target.value)}
                    placeholder="#kampanya #marka"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="required_mentions">Zorunlu Etiketler</Label>
                  <Input
                    id="required_mentions"
                    value={form.required_mentions}
                    onChange={(e) => handleInputChange('required_mentions', e.target.value)}
                    placeholder="@marka"
                  />
                </div>
                <p className="col-span-2 text-xs text-gray-500">
                  Kullanıcı gönderi linkini yolladığında gönderinin kendi hesabına ait olduğu, görev başladıktan sonra paylaşıldığı
                  ve bu hashtag/etiketleri içerdiği otomatik kontrol edilir.
                </p>
              </div>
            )}

            {/* Capacity & Deadline */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="capacity">Kontenjan *</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  value={form.capacity}
                  onChange={(e) => handleInputChange('capacity', e.target.value)}
                  placeholder="Kaç kişi tamamlayabilir?"
                  data-testid="task-capacity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deadline">Son Tarih</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={form.deadline}
                  onChange={(e) => handleInputChange('deadline', e.target.value)}
                  data-testid="task-deadline-input"
                />
              </div>
            </div>

            {/* Payment */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment_amount">Görev Baz TL / Ödeme</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.payment_amount}
                  onChange={(e) => handleInputChange('payment_amount', e.target.value)}
                  placeholder="Gönüllü hariç ödeme, havuzda minimum TL"
                  data-testid="task-payment-input"
                />
              </div>
            </div>

            {formIsPool && (
              <div className="space-y-4 rounded-lg border border-brand-line bg-brand-yellow-soft/60 p-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-800">Havuz Formülü</h4>
                  <p className="text-xs text-gray-500">
                    Rj = R0 + a*A0*(Sj/S0)^α*h_erişim + (1-a)*B0*(Cj/C0)^β*h_performans
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">A0 Erişim Birim Değeri</Label>
                    <Input type="number" min="0" step="0.01" value={formPricingConfig.A0} onChange={(e) => updatePricingConfig('A0', Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">B0 Dönüşüm Birim Değeri</Label>
                    <Input type="number" min="0" step="0.01" value={formPricingConfig.B0} onChange={(e) => updatePricingConfig('B0', Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">a Erişim Katsayısı</Label>
                    <Input type="number" min="0" max="1" step="0.01" value={formPricingConfig.a} onChange={(e) => updatePricingConfig('a', Number(e.target.value))} />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">S0 Referans Erişim</Label>
                    <Input type="number" min="1" step="1" value={formPricingConfig.S0} onChange={(e) => updatePricingConfig('S0', Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">α Erişim Üssü</Label>
                    <Input type="number" min="0" max="2" step="0.01" value={formPricingConfig.alpha} onChange={(e) => updatePricingConfig('alpha', Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">C0 Referans Dönüşüm</Label>
                    <Input type="number" min="1" step="1" value={formPricingConfig.C0} onChange={(e) => updatePricingConfig('C0', Number(e.target.value))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">β Dönüşüm Üssü</Label>
                    <Input type="number" min="0" max="2" step="0.01" value={formPricingConfig.beta} onChange={(e) => updatePricingConfig('beta', Number(e.target.value))} />
                  </div>
                </div>

                {[
                  ['reach_quality', 'Erişim Kalitesi'],
                  ['perf_quality', 'Performans Kalitesi'],
                ].map(([block, label]) => (
                  <div key={block} className="space-y-3 rounded-md border bg-white p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{label} λ</Label>
                        <Input type="number" min="0" max="1" step="0.01" value={formPricingConfig[block].lambda} onChange={(e) => updateQualityConfig(block, 'lambda', Number(e.target.value))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{label} γ</Label>
                        <Input type="number" min="0" max="3" step="0.01" value={formPricingConfig[block].gamma} onChange={(e) => updateQualityConfig(block, 'gamma', Number(e.target.value))} />
                      </div>
                    </div>
                    {(formPricingConfig[block].kpis || []).map((kpi, index) => (
                      <div key={`${block}-${index}`} className="grid grid-cols-[1fr_100px_auto] gap-2">
                        <Input
                          value={kpi.name || ''}
                          onChange={(e) => updateQualityKpi(block, index, 'name', e.target.value)}
                          placeholder="Yan KPI adı"
                        />
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={kpi.weight}
                          onChange={(e) => updateQualityKpi(block, index, 'weight', Number(e.target.value))}
                          placeholder="%"
                        />
                        <Button type="button" variant="outline" onClick={() => removeQualityKpi(block, index)}>
                          Sil
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => addQualityKpi(block)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {label} KPI Ekle
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Requirements Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-sm text-gray-700 mb-3">Gereksinimler (Opsiyonel)</h4>
              
              {/* Follower Requirements (TikTok / YouTube are paused) */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="min_instagram_followers">Min Instagram Takipçi</Label>
                  <Input
                    id="min_instagram_followers"
                    type="number"
                    min="0"
                    value={form.min_instagram_followers}
                    onChange={(e) => handleInputChange('min_instagram_followers', e.target.value)}
                    data-testid="task-min-ig-input"
                  />
                </div>
              </div>

              {/* City & Age Requirements */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Şehir</Label>
                  <Select value={form.required_city || 'all'} onValueChange={(v) => handleInputChange('required_city', v === 'all' ? '' : v)}>
                    <SelectTrigger data-testid="task-city-select">
                      <SelectValue placeholder="Tüm şehirler" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm şehirler</SelectItem>
                      {CITIES.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min_age">Min Yaş</Label>
                  <Input
                    id="min_age"
                    type="number"
                    min="13"
                    max="100"
                    value={form.min_age}
                    onChange={(e) => handleInputChange('min_age', e.target.value)}
                    data-testid="task-min-age-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_age">Max Yaş</Label>
                  <Input
                    id="max_age"
                    type="number"
                    min="13"
                    max="100"
                    value={form.max_age}
                    onChange={(e) => handleInputChange('max_age', e.target.value)}
                    data-testid="task-max-age-input"
                  />
                </div>
              </div>
            </div>

            {/* Submission Type */}
            <div className="space-y-2">
              <Label>Gönderim Türü</Label>
              <Select value={form.submission_type} onValueChange={(v) => handleInputChange('submission_type', v)}>
                <SelectTrigger data-testid="task-submission-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">Sadece Link</SelectItem>
                  <SelectItem value="image">Sadece Görsel</SelectItem>
                  <SelectItem value="both">Link + Görsel</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Kullanıcının görev gönderirken ne yüklemesi gerektiğini belirler</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-task-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Görevi Sil</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            <strong>{selectedTask?.title}</strong> görevini silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-task"
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
